import {
  Color,
  CompositeLayer,
  CompositeLayerProps,
  LayerContext,
  Position,
  UpdateParameters,
  PickingInfo,
  Layer,
} from "@deck.gl/core";
import { TextLayer, ScatterplotLayer, IconLayer, IconLayerProps } from "@deck.gl/layers";
import { Polygon, MultiPolygon, Point, Feature, BBox } from "@turf/helpers";
import { bboxPolygon, polygonSmooth } from "@turf/turf";
import { identity } from "lodash";

import {
  cellAddressToTileBBox,
  ClusteringConfig,
  DEFAULT_CLUSTERING_CONFIG,
} from "@landtechnologies/search-layer-contracts";

import { BaseMapType, mapStore } from "src/js/stores/map/store";
import { LogEventProps } from "react-migration/lib/util/logEvent";
import { logEventV2 } from "react-migration/lib/util/logEventV2";
import { isMobile } from "react-migration/lib/theme/hooks/useIsMobile";
import {
  EXPLORER_WIDTH_DESKTOP_PX,
  LOCATION_SEARCH_HEIGHT_MOBILE_PX,
  NAVBAR_HEIGHT_MOBILE_PX,
  WORKBENCH_HEIGHT_MOBILE_PC,
  WORKBENCH_WIDTH_DESKTOP_PX,
} from "react-migration/constants";
import { BBox2d } from "@turf/helpers/lib/geojson";

const FIT_BOUNDS_PADDING_PX = 20;
const CLUSTER_HOVER_SCALE = 1.1;
const CLUSTER_TEXT_SIZE_PX = 16;

type UnparsedOpenSearchDocument = Record<string, unknown>;
type OnClickEvent = Parameters<NonNullable<CompositeLayerProps["onClick"]>>[1];

type HasGeometry = {
  geometry: Polygon | MultiPolygon | Point;
};

type CellBounds = {
  top_left: {
    lat: number;
    lon: number;
  };
  bottom_right: {
    lat: number;
    lon: number;
  };
};

type RawClusterPoint<D extends HasGeometry, A extends Record<string, unknown>> = A & {
  cell: string;
  count: number;
  results: D[];
  centroid: {
    location: {
      lat: number;
      lon: number;
    };
    count: number;
  };
  bounds: CellBounds;
};

export type HoveredClusterInfo<C extends ParsedClusterPoint> = {
  cluster: C;
  bbox: BBox2d;
  width: number;
  height: number;
};

export type ParsedClusterPoint<
  D extends HasGeometry = HasGeometry,
  A extends Record<string, unknown> = Record<string, unknown>
> = RawClusterPoint<D, A> & {
  /**
   * Average of the coordinates of the cluster
   */
  centroidCoordinates: Position;
  /**
   * The centre coordinates of the cell
   */
  cellCoordinates: Position;
  /**
   * A polygon representing the grid cell used for clustering
   */
  cellClusterArea: Polygon;
  /**
   * A polygon representing the bounds of contained features within the cluster
   */
  cellClusterBounds: Polygon;
};

export type ClusteredViewportSearchMapLayerProps<
  D extends HasGeometry,
  P extends Record<string, unknown> = Record<string, unknown>,
  A extends Record<string, unknown> = Record<string, never>,
  F extends Record<string, unknown> = Record<string, unknown>
> = CompositeLayerProps & {
  url: string;
  renderFeatureLayers: (features: Feature<Point | Polygon | MultiPolygon, P>[]) => Layer | Layer[];
  filters: F;
  skip?: boolean;
  googleMap: google.maps.Map | null;
  mapType?: BaseMapType;
  parseOpenSearchFeatures?: (
    d: UnparsedOpenSearchDocument
  ) => Feature<Point | Polygon | MultiPolygon, P>[] | Feature<Point | Polygon | MultiPolygon, P>;
  getClusterFillColor?: Color | ((d: ParsedClusterPoint<D, A>) => Color);
  getClusterLineColor?: Color | ((d: ParsedClusterPoint<D, A>) => Color);
  getClusterTextSize?: number | ((d: ParsedClusterPoint<D, A>) => number);
  getClusterRadius: number | ((d: ParsedClusterPoint<D, A>) => number);
  getClusterIcon?: IconLayerProps<ParsedClusterPoint<D, A>>["getIcon"];
  clusterIconAtlas?: IconLayerProps<ParsedClusterPoint<D, A>>["iconAtlas"];
  clusterIconMapping?: IconLayerProps<ParsedClusterPoint<D, A>>["iconMapping"];
  onFetchClusterError?: (error: Error) => void;
  clusterEventProperties: {
    selectionLayerId: string;
  } & LogEventProps;
  /** Optional clustering configuration - uses DEFAULT_CLUSTERING_CONFIG if not provided */
  clusteringConfig?: ClusteringConfig;
  onClusterHover?: (cluster: HoveredClusterInfo<ParsedClusterPoint<D, A>> | null) => void;
  /** An array of dependencies, if any value changes then it will force a refetch of the data */
  dataDeps?: unknown[];
};

export class ClusteredViewportSearchMapLayer<
  /** Raw OpenSearch document returned as a result item */
  D extends HasGeometry,
  /** Properties of the feature parsed from D */
  P extends Record<string, unknown> = Record<string, unknown>,
  /** Aggregations of the features parsed from D */
  A extends Record<string, unknown> = Record<string, never>,
  /** Filters to apply to the query */
  F extends Record<string, unknown> = Record<string, unknown>
> extends CompositeLayer<ClusteredViewportSearchMapLayerProps<D, P, A, F>> {
  static layerName = "ClusteredViewportSearchMapLayer";

  declare state: {
    /** Returned clusters after they are parsed by parseClusterPoint and parseOpenSearchFeatures */
    clusters: ParsedClusterPoint<D, A>[];
    /** Cluster features that have been exploded into individual features to be rendered by renderFeatureLayers */
    explodedFeatures: Feature<Point | Polygon | MultiPolygon, P>[];
    /** Whether the layer is currently loading */
    loading: boolean;
    /** Used to abort in flight cluster requests if viewport changes */
    abortController: AbortController | null;
    /** The timeout for the current request. Used to debounce cluster requests */
    fetchTimeout: number | null;
    /** The current hovered cluster */
    hoveredCluster: ParsedClusterPoint<D, A> | null;
  };

  constructor(props: ClusteredViewportSearchMapLayerProps<D, P, A, F>) {
    super({
      ...props,
      onClick: (...args) => this.onClickHandler(...args),
      onHover: (...args) => this.onHoverHandler(...args),
    });
  }

  initializeState() {
    this.setState({
      clusters: [],
      explodedFeatures: [],
      abortController: null,
      fetchTimeout: null,
      loading: false,
      hoveredCluster: null,
    });
  }

  static get defaultProps() {
    return {
      clusteringConfig: DEFAULT_CLUSTERING_CONFIG,
    };
  }

  override shouldUpdateState({
    changeFlags,
  }: UpdateParameters<Layer<ClusteredViewportSearchMapLayerProps<D, P, A>>>): boolean {
    return changeFlags.propsOrDataChanged || changeFlags.viewportChanged;
  }

  override finalizeState(context: LayerContext) {
    super.finalizeState(context);

    this.state.clusters = [];
    this.state.explodedFeatures = [];

    if (this.state.fetchTimeout) {
      window.clearTimeout(this.state.fetchTimeout);
    }

    this.props.onClusterHover?.(null);

    if (this.state.abortController) {
      this.state.abortController.abort();
      this.state.abortController = null;
    }
  }

  updateState({ changeFlags, oldProps, props }: UpdateParameters<this>) {
    if (!props.visible) {
      return;
    }

    const prevDataDeps = oldProps.dataDeps ?? [];
    const dataDeps = props.dataDeps ?? [];
    const dataDepsChanged = dataDeps.some((dep, i) => dep !== prevDataDeps[i]);

    if (
      dataDepsChanged ||
      changeFlags.viewportChanged ||
      (changeFlags.propsChanged && oldProps.filters !== props.filters)
    ) {
      this.debouncedFetchClusters();
    }
  }

  private calculateClusterFeatures(rawClusterData: RawClusterPoint<D, A>[], precision: number) {
    const { parseOpenSearchFeatures = identity, clusteringConfig } = this.props;
    const { forceExplodePrecision, clusterExplosionThreshold } = clusteringConfig!;
    const explodeAll = precision >= forceExplodePrecision;
    const parsedClusters = rawClusterData?.map(parseClusterPoint) || [];

    const unexplodedClusters = explodeAll
      ? []
      : parsedClusters.filter(({ count, results }) => count >= clusterExplosionThreshold || !results?.length);

    const unexplodedCells = unexplodedClusters.map((cluster) => cluster.cell);
    const explodedClusters = parsedClusters.filter((cluster) => !unexplodedCells.includes(cluster.cell));
    const explodedFeatures = explodedClusters
      .flatMap((cluster) => cluster!.results ?? [])
      .flatMap((hit) => parseOpenSearchFeatures(hit));

    return {
      clusters: unexplodedClusters,
      explodedFeatures,
    };
  }

  private isClusterInteraction(info: PickingInfo<ParsedClusterPoint<D, A>>): boolean {
    const sourceLayerId = info?.sourceLayer?.id || "";
    return sourceLayerId.endsWith("clusters");
  }

  private preventEventBubbling(event: OnClickEvent) {
    // @ts-expect-error incorrect typings
    event.srcEvent.domEvent?.preventDefault?.();
  }

  private onClickHandler(info: PickingInfo<ParsedClusterPoint<D, A>>, event: OnClickEvent) {
    this.props.onClusterHover?.(null);

    if (!info.object) {
      return;
    }

    if (this.isClusterInteraction(info)) {
      this.preventEventBubbling(event);
      this.handleClusterClick(info.object);
    }
  }

  private onHoverHandler(info: PickingInfo<ParsedClusterPoint<D, A>>, event: OnClickEvent) {
    if (this.isClusterInteraction(info)) {
      this.preventEventBubbling(event);

      const hoveredCluster = info.object;

      if (hoveredCluster && this.state.hoveredCluster?.cell !== hoveredCluster.cell) {
        this.setState({ hoveredCluster });
        const screenPosition = this.getClusterScreenPosition(hoveredCluster, { hovered: true });
        this.props.onClusterHover?.({ cluster: hoveredCluster, ...screenPosition });
      } else if (!hoveredCluster) {
        this.setState({ hoveredCluster: null });
        this.props.onClusterHover?.(null);
      }
    }
  }

  private handleClusterClick(cluster: ParsedClusterPoint<D, A>) {
    const { googleMap } = this.props;

    if (googleMap && cluster) {
      googleMap.fitBounds(
        new google.maps.LatLngBounds(
          { lat: cluster.bounds.top_left.lat, lng: cluster.bounds.top_left.lon },
          { lat: cluster.bounds.bottom_right.lat, lng: cluster.bounds.bottom_right.lon }
        ),
        getFitBoundsPadding()
      );

      this.setState({ hoveredCluster: null });

      logEventV2({
        name: "Bundle Map Click",
        properties: {
          ...this.props.clusterEventProperties,
          clickMethod: "left",
          action: "cluster_click",
          zoom: googleMap.getZoom(),
          clusterAddress: cluster.cell,
        },
      });
    }
  }

  private debouncedFetchClusters() {
    if (this.state.fetchTimeout) {
      window.clearTimeout(this.state.fetchTimeout);
    }

    const timeout = window.setTimeout(() => this.fetchClusters(), 500);

    this.state.fetchTimeout = timeout;
  }

  /**
   * Given a cluster, calculate the bounding box in screen space
   */
  private getClusterScreenPosition(
    cluster: ParsedClusterPoint<D, A>,
    { hovered }: { hovered: boolean }
  ): { bbox: BBox2d; width: number; height: number } {
    const { getClusterRadius } = this.props;
    const { project } = this.context.viewport;
    const { centroidCoordinates } = cluster;

    const hoveredMultiplier = hovered ? CLUSTER_HOVER_SCALE : 1;
    const radius = accessor(getClusterRadius)(cluster) * hoveredMultiplier;
    const [x, y] = project(centroidCoordinates as number[]);
    const bbox: BBox2d = [x - radius, y - radius, x + radius, y + radius];

    return { bbox, width: radius * 2, height: radius * 2 };
  }

  private async fetchClusters() {
    if (this.props.skip) {
      return;
    }

    if (this.state.abortController) {
      this.state.abortController.abort();
    }

    const abortController = new AbortController();
    this.state.abortController = abortController;

    const bounds = this.context.viewport.getBounds();
    const [west, south, east, north] = bounds;
    const precision = Math.round(this.context.viewport.zoom) + 1;
    const { url, filters, onFetchClusterError } = this.props;

    try {
      this.setState({ loading: true });

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          north,
          south,
          east,
          west,
          precision,
          filters,
        }),
        signal: abortController.signal,
      });
      const data: RawClusterPoint<D, A>[] = (await response.json()) || [];

      this.setState({
        ...this.calculateClusterFeatures(data, precision),
        loading: false,
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        this.state.abortController = null;
        return;
      }

      onFetchClusterError?.(error as Error);

      this.setState({
        ...this.calculateClusterFeatures([], precision),
        loading: false,
      });
    }
  }

  /** Returns true if all async resources are loaded */
  get isLoaded(): boolean {
    // Track the explicit loading done by this layer
    return !this.state.loading && super.isLoaded;
  }

  renderLayers() {
    const { clusters, explodedFeatures, hoveredCluster } = this.state;

    const { renderFeatureLayers } = this.props;
    const { getClusterIcon, clusterIconAtlas, clusterIconMapping } = this.props;
    const { getClusterFillColor, getClusterLineColor, getClusterRadius, getClusterTextSize } = this.props;
    const shouldUseIconLayer = getClusterIcon && clusterIconAtlas && clusterIconMapping;

    const featureLayersResult = renderFeatureLayers(explodedFeatures);
    const featureLayers = Array.isArray(featureLayersResult) ? featureLayersResult : [featureLayersResult];

    return [
      ...featureLayers,
      shouldUseIconLayer
        ? new IconLayer<ParsedClusterPoint<D, A>>(
            this.getSubLayerProps({
              id: `${this.props.id}-icon-clusters`,
              data: clusters,
              getSize: scaleClusterAccessorOnHover(
                radiusToIconSizeAccessor(getClusterRadius),
                hoveredCluster?.cell,
                CLUSTER_HOVER_SCALE
              ),
              getIcon: getClusterIcon,
              iconAtlas: clusterIconAtlas,
              iconMapping: clusterIconMapping,
              getPosition: (d: ParsedClusterPoint<D, A>) => d.centroidCoordinates,
              pickable: true,
              opacity: 1,
              updateTriggers: {
                getSize: [hoveredCluster?.cell],
              },
              transitions: {
                getSize: 100,
              },
            })
          )
        : new ScatterplotLayer<ParsedClusterPoint<D, A>>(
            this.getSubLayerProps({
              id: `${this.props.id}-scatter-clusters`,
              data: clusters,
              getRadius: scaleClusterAccessorOnHover(getClusterRadius, hoveredCluster?.cell, CLUSTER_HOVER_SCALE),
              getFillColor: getClusterFillColor,
              getLineColor: getClusterLineColor || [255, 255, 255, 255],
              getPosition: (d: ParsedClusterPoint<D, A>) => d.centroidCoordinates,
              pickable: true,
              opacity: 1,
              stroked: true,
              filled: true,
              radiusUnits: "pixels",
              lineWidthUnits: "pixels",
              getLineWidth: 3,
              updateTriggers: {
                getRadius: [hoveredCluster?.cell],
              },
              transitions: {
                getRadius: 100,
              },
            })
          ),
      new TextLayer<ParsedClusterPoint<D, A>>(
        this.getSubLayerProps({
          id: `${this.props.id}-clusters-text`,
          data: clusters,
          pickable: false,
          getPosition: (d: ParsedClusterPoint<D, A>) => d.centroidCoordinates,
          getText: getClusterCountDisplay,
          getColor: [255, 255, 255],
          getSize: scaleClusterAccessorOnHover(
            getClusterTextSize ?? CLUSTER_TEXT_SIZE_PX,
            hoveredCluster?.cell,
            CLUSTER_HOVER_SCALE
          ),
          updateTriggers: {
            getSize: [hoveredCluster?.cell],
          },
          transitions: {
            getSize: 100,
          },
          fontWeight: 500,
          getTextAnchor: "middle",
          getAlignmentBaseline: "center",
          fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
        })
      ),
    ];
  }
}

function cellBoundsToPolygon({ top_left, bottom_right }: CellBounds): Polygon {
  const { lat: topLeftLat, lon: topLeftLon } = top_left;
  const { lat: bottomRightLat, lon: bottomRightLon } = bottom_right;
  const bbox: BBox = [topLeftLon, topLeftLat, bottomRightLon, bottomRightLat];
  return bboxPolygon(bbox).geometry;
}

function parseClusterPoint<D extends HasGeometry, A extends Record<string, unknown>>(
  rawClusterPoint: RawClusterPoint<D, A>
): ParsedClusterPoint<D, A> {
  const cellCoordinates = cellAddressToCoords(rawClusterPoint.cell);
  const cellPolygon = bboxPolygon(cellAddressToTileBBox(rawClusterPoint.cell) as BBox);
  const cellClusterBounds = cellBoundsToPolygon(rawClusterPoint.bounds);

  return {
    ...rawClusterPoint,
    cellCoordinates,
    cellClusterArea: polygonSmooth(cellPolygon, {
      iterations: 3,
    }).features[0].geometry,
    cellClusterBounds: polygonSmooth(cellClusterBounds, {
      iterations: 3,
    }).features[0].geometry,
    centroidCoordinates: [rawClusterPoint.centroid.location.lon, rawClusterPoint.centroid.location.lat],
  };
}

function getClusterCountDisplay({ count }: ParsedClusterPoint) {
  if (count < 1000) {
    return count.toString();
  }
  if (count < 10000) {
    return (count / 1000).toFixed(1) + "k";
  }
  if (count < 1000000) {
    return Math.round(count / 1000) + "k";
  }
  return (count / 1000000).toFixed(1) + "m";
}

function cellAddressToCoords(cellAddress: string): Position {
  const [z, x, y] = cellAddress.split("/").map(Number);

  // Calculate the center of the tile
  const lng = ((x + 0.5) / Math.pow(2, z)) * 360 - 180;
  const lat = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 0.5)) / Math.pow(2, z)))) * 180) / Math.PI;

  return [lng, lat];
}

/**
 * Calculates the padding to apply to the map viewport to fit the bounds of the clusters
 * @returns The padding to apply to the map viewport to fit the bounds of the clusters
 */
function getFitBoundsPadding(): google.maps.Padding {
  let top = FIT_BOUNDS_PADDING_PX,
    right = FIT_BOUNDS_PADDING_PX,
    bottom = FIT_BOUNDS_PADDING_PX,
    left = FIT_BOUNDS_PADDING_PX;

  const { expandedLHS, showWorkbench } = mapStore;

  if (isMobile()) {
    top = LOCATION_SEARCH_HEIGHT_MOBILE_PX;

    if (showWorkbench) {
      // Note: NAVBAR_HEIGHT_MOBILE_PC is a percentage of the window height unlike the other constants
      bottom =
        ((window.innerHeight - NAVBAR_HEIGHT_MOBILE_PX) / 100) * WORKBENCH_HEIGHT_MOBILE_PC + NAVBAR_HEIGHT_MOBILE_PX;
    }
  } else {
    if (expandedLHS) left = EXPLORER_WIDTH_DESKTOP_PX;
    if (showWorkbench) right = WORKBENCH_WIDTH_DESKTOP_PX;
  }

  return { top, right, bottom, left };
}

/**
 * Wraps a cluster accessor function to scale the value when the cluster is hovered
 * @param accessor - The accessor function to wrap, must be a number or a function which returns a number
 * @param hoveredClusterCell - The cell address of the hovered cluster
 * @param scale - The scale factor to apply when the cluster is hovered
 * @returns A new accessor function that returns the scaled value
 */
function scaleClusterAccessorOnHover<D extends Pick<ParsedClusterPoint, "cell">>(
  valueOrAccessor: number | ((d: D) => number),
  hoveredClusterCell: string | undefined,
  scale: number
): (d: D) => number {
  const getValue = accessor(valueOrAccessor);

  return (d) => {
    const value = getValue(d);
    return d.cell === hoveredClusterCell ? value * scale : value;
  };
}

/**
 * Convert a clusters radius accessor (or value) into the size accessor for an icon layer
 */
function radiusToIconSizeAccessor<D extends Pick<ParsedClusterPoint, "cell">>(
  radiusValueOrAccessor: number | ((d: D) => number)
): (d: D) => number {
  const getRadius = accessor(radiusValueOrAccessor);

  return (d) => getRadius(d) * 2;
}

function accessor<D, V>(accesor: V | ((d: D) => V)): (d: D) => V {
  return typeof accesor === "function" ? (accesor as (d: D) => V) : () => accesor;
}
